/**
 * LOAMS Engineer Dashboard API Service
 * =====================================
 * Clean, organized API service for the Engineer Dashboard
 * 
 * INTEGRATION:
 * In your main api.js, add at the bottom:
 *   import { loamsEngineerApi } from './loamsEngineerApi';
 *   export { loamsEngineerApi };
 * 
 * Or merge the pythonApi configuration if needed.
 */

import axios from 'axios';

// ============================================================================
// AXIOS INSTANCE CONFIGURATION
// ============================================================================

const pythonApi = axios.create({
    baseURL: 'https://accountss-loams.onrender.com',
    timeout: 300000, // 5 minutes default
    withCredentials: true,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    },
    maxContentLength: 50 * 1024 * 1024,
    maxBodyLength: 50 * 1024 * 1024
});

// Request interceptor
pythonApi.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = token;
        }
        console.log('[LOAMS-ENG] Request:', config.method?.toUpperCase(), config.url);
        return config;
    },
    error => {
        console.error('[LOAMS-ENG] Request error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor
pythonApi.interceptors.response.use(
    response => {
        console.log('[LOAMS-ENG] Response:', response.status, response.config.url);
        return response;
    },
    error => {
        console.error('[LOAMS-ENG] Error:', error.response?.status, error.message);
        
        if (error.response?.status === 401) {
            localStorage.clear();
            window.location.href = '/login?session=expired';
        }
        
        return Promise.reject(error);
    }
);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatMonthLabel = (monthStr) => {
    try {
        if (!monthStr || monthStr === 'Unknown') return 'Unknown';
        
        let month = String(monthStr).trim();
        if (month.includes('T')) month = month.substring(0, 7);
        if (month.includes(' ')) month = month.substring(0, 7);
        
        if (/^\d{4}-\d{2}$/.test(month)) {
            const [year, monthNum] = month.split('-');
            const date = new Date(parseInt(year), parseInt(monthNum) - 1);
            return `${date.toLocaleString('default', { month: 'short' })} ${year}`;
        }
        return monthStr;
    } catch (error) {
        return monthStr || 'Unknown';
    }
};

const formatDateDubai = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
        return new Date(dateStr).toLocaleDateString('en-AE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'Asia/Dubai'
        });
    } catch {
        return 'N/A';
    }
};

// ============================================================================
// LOAMS ENGINEER API SERVICE
// ============================================================================

export const loamsEngineerApi = {
    
    // ==========================================================================
    // YEARS & OVERVIEW
    // ==========================================================================
    
    /**
     * Get all available years from both tables
     */
    getAvailableYears: async () => {
        console.log('[LOAMS-ENG] Fetching available years');
        try {
            const response = await pythonApi.get('/api/years/available');
            return response.data;
        } catch (error) {
            console.error('[LOAMS-ENG] Get years error:', error);
            // Fallback to default years
            return { success: true, years: [2025, 2024, 2023], count: 3 };
        }
    },
    
    /**
     * Get dashboard overview statistics
     */
    getDashboardOverview: async (year = null) => {
        console.log('[LOAMS-ENG] Fetching dashboard overview for year:', year);
        try {
            const response = await pythonApi.get('/api/dashboard/overview', {
                params: { year }
            });
            return response.data;
        } catch (error) {
            console.error('[LOAMS-ENG] Dashboard overview error:', error);
            throw error;
        }
    },
    
    // ==========================================================================
    // CLUSTERS & AGREEMENTS
    // ==========================================================================
    
    /**
     * Get list of clusters with year filtering
     */
    getClusters: async (year = null) => {
        console.log('[LOAMS-ENG] Fetching clusters for year:', year);
        try {
            const response = await pythonApi.get('/api/file/clusters', {
                params: { year }
            });
            return response.data;
        } catch (error) {
            console.error('[LOAMS-ENG] Get clusters error:', error);
            throw error;
        }
    },
    
    /**
     * Get agreements for a specific cluster
     */
    getClusterAgreements: async (cluster, year = null) => {
        console.log('[LOAMS-ENG] Fetching agreements for cluster:', cluster);
        try {
            const response = await pythonApi.get(`/api/file/clusters/${encodeURIComponent(cluster)}/agreements`, {
                params: { year }
            });
            return response.data;
        } catch (error) {
            console.error('[LOAMS-ENG] Get agreements error:', error);
            throw error;
        }
    },
    
    /**
     * Get full agreement details including auxillarsss, trn, address
     */
    getAgreementDetails: async (cluster, agreementRef) => {
        console.log('[LOAMS-ENG] Fetching agreement details:', { cluster, agreementRef });
        try {
            const response = await pythonApi.get('/api/agreement-details', {
                params: { cluster, agreementRef }
            });
            return response.data;
        } catch (error) {
            console.error('[LOAMS-ENG] Get agreement details error:', error);
            throw error;
        }
    },
    
    // ==========================================================================
    // PAYEE OPERATIONS
    // ==========================================================================
    
    /**
     * Get all unique payees with PO counts
     */
    getUniquePayees: async (year = null) => {
        console.log('[LOAMS-ENG] Fetching unique payees');
        try {
            const response = await pythonApi.get('/api/file/unique-payees', {
                params: { year }
            });
            return response.data;
        } catch (error) {
            console.error('[LOAMS-ENG] Get payees error:', error);
            throw error;
        }
    },
    
    /**
     * Filter POs by payee
     */
    getPOsByPayee: async (payee, year = null) => {
        console.log('[LOAMS-ENG] Fetching POs for payee:', payee);
        try {
            const response = await pythonApi.get('/api/file/pos-by-payee', {
                params: { payee, year }
            });
            return response.data;
        } catch (error) {
            console.error('[LOAMS-ENG] Get POs by payee error:', error);
            throw error;
        }
    },
    
    // ==========================================================================
    // BULK GENERATION
    // ==========================================================================
    
    /**
     * Get all agreements for a year (for bulk operations)
     */
    getAgreementsByYear: async (year, payeeFilter = null, serviceTypeFilter = null) => {
        console.log('[LOAMS-ENG] Fetching agreements by year:', year);
        try {
            const response = await pythonApi.get('/api/file/agreements-by-year', {
                params: { 
                    year, 
                    payee: payeeFilter,
                    service_type: serviceTypeFilter
                }
            });
            return response.data;
        } catch (error) {
            console.error('[LOAMS-ENG] Get agreements by year error:', error);
            throw error;
        }
    },
    
    /**
     * Get agreements valid for a specific month with certificate status
     */
    getAgreementsByMonth: async (month, payeeFilter = null, serviceTypeFilter = null) => {
        console.log('[LOAMS-ENG] Fetching agreements for month:', month);
        try {
            const response = await pythonApi.get('/api/file/agreements-by-month', {
                params: { 
                    month, 
                    payee: payeeFilter,
                    service_type: serviceTypeFilter
                },
                timeout: 120000 // 2 minutes for complex query
            });
            return response.data;
        } catch (error) {
            console.error('[LOAMS-ENG] Get agreements by month error:', error);
            throw error;
        }
    },
    
    // ==========================================================================
    // CERTIFICATE OPERATIONS
    // ==========================================================================
    
    certificates: {
        /**
         * List certificates for a cluster/agreement
         */
        list: async (cluster, agreementRef) => {
            console.log('[LOAMS-ENG] Listing certificates:', { cluster, agreementRef });
            try {
                const response = await pythonApi.get('/api/certificate/list', {
                    params: { 
                        cluster, 
                        agreement_ref: agreementRef 
                    },
                    timeout: 60000
                });
                return response.data;
            } catch (error) {
                if (error.response?.status === 404) {
                    return { success: true, certificates: [], count: 0 };
                }
                throw error;
            }
        },
        
        /**
         * Enhanced certificate listing with multiple search strategies
         */
        listEnhanced: async (cluster, agreementRef) => {
            console.log('[LOAMS-ENG] Enhanced certificate list:', { cluster, agreementRef });
            try {
                const response = await pythonApi.get('/api/certificate/list-enhanced', {
                    params: { 
                        cluster: cluster.trim(), 
                        agreement_ref: agreementRef.trim() 
                    },
                    timeout: 60000
                });
                
                // Transform certificates for consistent frontend usage
                if (response.data?.success && response.data.certificates) {
                    response.data.certificates = response.data.certificates.map((cert, index) => ({
                        ...cert,
                        id: cert.id || `cert_${index}`,
                        sizeFormatted: formatFileSize(cert.size),
                        lastModifiedFormatted: formatDateDubai(cert.last_modified),
                        monthLabel: formatMonthLabel(cert.month)
                    }));
                }
                
                return response.data;
            } catch (error) {
                if (error.response?.status === 404) {
                    return { 
                        success: true, 
                        certificates: [], 
                        count: 0,
                        message: 'No certificates found for this PO'
                    };
                }
                throw error;
            }
        },
        
        /**
         * Check if certificate exists for a specific month
         */
        checkExists: async (cluster, agreementRef, month) => {
            console.log('[LOAMS-ENG] Checking certificate exists:', { cluster, agreementRef, month });
            try {
                const response = await pythonApi.get('/api/certificate/check-exists', {
                    params: { cluster, agreement_ref: agreementRef, month }
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS-ENG] Check exists error:', error);
                return { exists: false };
            }
        },
        
        /**
         * Upload a certificate
         */
        upload: async (formData, onProgress = null) => {
            console.log('[LOAMS-ENG] Uploading certificate');
            try {
                const response = await pythonApi.post('/api/certificate/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    timeout: 120000,
                    onUploadProgress: (progressEvent) => {
                        if (onProgress && progressEvent.total) {
                            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            onProgress(percent);
                        }
                    }
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS-ENG] Upload error:', error);
                throw error;
            }
        },
        
        /**
         * Bulk upload multiple certificates
         */
        bulkUpload: async (files, metadata) => {
            console.log('[LOAMS-ENG] Bulk uploading certificates:', files.length);
            try {
                const formData = new FormData();
                
                files.forEach((file, index) => {
                    formData.append('files', file);
                });
                
                formData.append('metadata', JSON.stringify(metadata));
                
                const response = await pythonApi.post('/api/certificate/bulk-upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    timeout: 600000 // 10 minutes for bulk
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS-ENG] Bulk upload error:', error);
                throw error;
            }
        },
        
        /**
         * Get preview URL for a certificate
         */
        getPreviewUrl: async (certificateKey) => {
            console.log('[LOAMS-ENG] Getting preview URL:', certificateKey);
            try {
                const encodedKey = encodeURIComponent(certificateKey);
                const response = await pythonApi.get(`/api/certificate/preview/${encodedKey}`, {
                    timeout: 20000
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS-ENG] Preview URL error:', error);
                throw error;
            }
        },
        
        /**
         * Download a single certificate
         */
        download: async (certificateKey, filename = null) => {
            console.log('[LOAMS-ENG] Downloading certificate:', certificateKey);
            try {
                // First get the presigned URL
                const previewData = await loamsEngineerApi.certificates.getPreviewUrl(certificateKey);
                
                if (!previewData?.url) {
                    throw new Error('Failed to get download URL');
                }
                
                // Fetch the file
                const response = await fetch(previewData.url, { mode: 'cors' });
                if (!response.ok) throw new Error(`Download failed: ${response.status}`);
                
                const blob = await response.blob();
                
                // Create download
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename || certificateKey.split('/').pop() || 'certificate.pdf';
                link.style.display = 'none';
                
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                
                return { success: true, filename: link.download };
            } catch (error) {
                console.error('[LOAMS-ENG] Download error:', error);
                throw error;
            }
        },
        
        /**
         * Download multiple certificates as ZIP
         */
        downloadBatch: async (certificateKeys) => {
            console.log('[LOAMS-ENG] Batch downloading:', certificateKeys.length, 'certificates');
            try {
                const response = await fetch(`${pythonApi.defaults.baseURL}/api/certificates/download-batch`, {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Accept': 'application/zip'
                    },
                    body: JSON.stringify({ keys: certificateKeys })
                });
                
                if (!response.ok) throw new Error(`Batch download failed: ${response.status}`);
                
                const blob = await response.blob();
                
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `certificates-batch-${new Date().toISOString().split('T')[0]}.zip`;
                
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
                
                return { success: true, count: certificateKeys.length };
            } catch (error) {
                console.error('[LOAMS-ENG] Batch download error:', error);
                throw error;
            }
        },
        
        /**
         * Validate month before upload (sequential validation)
         */
        validateMonth: async (cluster, agreementRef, month) => {
            console.log('[LOAMS-ENG] Validating month:', { cluster, agreementRef, month });
            try {
                const response = await pythonApi.post('/api/certificate/validate-month', {
                    cluster: cluster.trim(),
                    agreement_ref: agreementRef.trim(),
                    month: month.trim()
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS-ENG] Validate month error:', error);
                throw error;
            }
        }
    },
    
    // ==========================================================================
    // GPT-4 VALIDATION
    // ==========================================================================
    
    gpt4Validation: {
        /**
         * Check GPT-4 service health
         */
        checkHealth: async () => {
            console.log('[LOAMS-ENG] Checking GPT-4 health');
            try {
                const response = await pythonApi.get('/api/certificate/gpt4-health', {
                    timeout: 10000
                });
                return response.data;
            } catch (error) {
                return {
                    status: 'error',
                    api_working: false,
                    message: error.message
                };
            }
        },
        
        /**
         * Validate certificate content using GPT-4 Vision
         */
        validateCertificate: async (file, expectedPO, expectedMonth) => {
            console.log('[LOAMS-ENG] GPT-4 validating certificate');
            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('expected_po', expectedPO);
                formData.append('expected_month', expectedMonth);
                
                const response = await pythonApi.post('/api/certificate/validate-gpt4', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    timeout: 60000
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS-ENG] GPT-4 validation error:', error);
                // Return fallback result
                return {
                    success: true,
                    validation: {
                        validation_result: { overall_valid: true },
                        confidence: { overall_confidence: 0.5 }
                    },
                    fallback: true,
                    warning: 'AI validation unavailable, file accepted pending manual review'
                };
            }
        }
    },
    
    // ==========================================================================
    // REPORTS
    // ==========================================================================
    
    reports: {
        /**
         * Get certificate status report
         */
        getCertificateReport: async (year = null, month = null, payeeFilter = null) => {
            console.log('[LOAMS-ENG] Fetching certificate report');
            try {
                const response = await pythonApi.get('/api/certificates/report', {
                    params: { year, month, payee: payeeFilter },
                    timeout: 120000
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS-ENG] Report error:', error);
                throw error;
            }
        },
        
        /**
         * Get enhanced completion report with serial numbers
         */
        getCompletionReportEnhanced: async (year = '2025', month = 'all') => {
            console.log('[LOAMS-ENG] Fetching enhanced completion report');
            try {
                const params = new URLSearchParams();
                params.append('year', year);
                if (month !== 'all') params.append('month', month);
                
                const response = await pythonApi.get(`/api/certificates/completion-report-enhanced?${params.toString()}`, {
                    timeout: 120000
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS-ENG] Enhanced report error:', error);
                throw error;
            }
        },
        
        /**
         * Get missing certificates report
         */
        getMissingCertificates: async (year = '2025') => {
            console.log('[LOAMS-ENG] Fetching missing certificates report');
            try {
                const response = await pythonApi.get('/api/certificates/report/missing-certificates', {
                    params: { year },
                    timeout: 60000
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS-ENG] Missing certificates error:', error);
                throw error;
            }
        },
        
        /**
         * Get all months report
         */
        getAllMonthsReport: async (year = null) => {
            console.log('[LOAMS-ENG] Fetching all months report');
            try {
                const response = await pythonApi.get('/api/certificates/report/all-months', {
                    params: { year },
                    timeout: 120000
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS-ENG] All months report error:', error);
                throw error;
            }
        },
        
        /**
         * Export report to Excel
         */
        exportToExcel: async (reportData, reportType = 'completion') => {
            console.log('[LOAMS-ENG] Exporting report to Excel');
            try {
                // Dynamic import XLSX
                const XLSX = window.XLSX || (await import('xlsx')).default;
                
                if (!XLSX) {
                    throw new Error('XLSX library not available');
                }
                
                const workbook = XLSX.utils.book_new();
                
                // Format data based on report type
                const excelData = reportData.agreements.map((item, index) => ({
                    'S.No': index + 1,
                    'Serial Number': item.serial_number || '',
                    'Project': item.project || item.cluster || '',
                    'Agreement Ref': item.agreement_ref_no || item.ref_no || '',
                    'Service Type': item.service_type || 'General Service',
                    'Month': item.monthLabel || formatMonthLabel(item.month) || '',
                    'Contract Start': formatDateDubai(item.contract_start_date),
                    'Contract End': formatDateDubai(item.contract_finish_date),
                    'Contract Value': item.total_amount || item.contract_value || 0,
                    'Certificate Status': item.certificate_uploaded ? 'Uploaded' : 'Missing',
                    'Upload Date': item.upload_date ? formatDateDubai(item.upload_date) : '',
                    'Payee': item.payee || '',
                    'Auxillarsss': item.auxillarsss || '',
                    'TRN': item.trn || ''
                }));
                
                const worksheet = XLSX.utils.json_to_sheet(excelData);
                
                // Set column widths
                worksheet['!cols'] = [
                    { wch: 8 },  // S.No
                    { wch: 18 }, // Serial Number
                    { wch: 30 }, // Project
                    { wch: 20 }, // Agreement Ref
                    { wch: 25 }, // Service Type
                    { wch: 15 }, // Month
                    { wch: 15 }, // Contract Start
                    { wch: 15 }, // Contract End
                    { wch: 15 }, // Contract Value
                    { wch: 15 }, // Certificate Status
                    { wch: 15 }, // Upload Date
                    { wch: 25 }, // Payee
                    { wch: 20 }, // Auxillarsss
                    { wch: 20 }  // TRN
                ];
                
                // Add main sheet
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Certificate Report');
                
                // Add summary sheet
                const stats = reportData.statistics || {};
                const summaryData = [
                    ['Certificate Report Summary', ''],
                    ['Generated On', new Date().toLocaleString()],
                    ['', ''],
                    ['Statistics', ''],
                    ['Total Agreements', stats.total_agreements || 0],
                    ['Certificates Uploaded', stats.total_with_certificates || 0],
                    ['Missing Certificates', stats.missing_certificates || 0],
                    ['Completion Rate', `${stats.completion_rate || 0}%`],
                    ['Unique Projects', stats.unique_projects || 0]
                ];
                
                const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
                XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
                
                // Generate and download
                const filename = `Certificate_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
                XLSX.writeFile(workbook, filename);
                
                return { success: true, filename };
            } catch (error) {
                console.error('[LOAMS-ENG] Excel export error:', error);
                throw error;
            }
        }
    },
    
    // ==========================================================================
    // UTILITY METHODS
    // ==========================================================================
    
    utils: {
        formatFileSize,
        formatMonthLabel,
        formatDateDubai,
        
        /**
         * Generate months list between two dates
         */
        generateMonthsList: (startDate, endDate) => {
            const months = [];
            if (!startDate || !endDate) return months;
            
            const start = new Date(startDate);
            const end = new Date(endDate);
            
            let current = new Date(start.getFullYear(), start.getMonth(), 1);
            const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
            
            while (current <= endMonth) {
                months.push({
                    value: `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`,
                    label: current.toLocaleString('default', { month: 'long', year: 'numeric' })
                });
                current.setMonth(current.getMonth() + 1);
            }
            
            return months;
        },
        
        /**
         * Filter agreements by search term
         */
        filterAgreements: (agreements, searchTerm) => {
            if (!searchTerm) return agreements;
            const term = searchTerm.toLowerCase();
            return agreements.filter(a => 
                a.ref_no?.toLowerCase().includes(term) ||
                a.cluster?.toLowerCase().includes(term) ||
                a.payee?.toLowerCase().includes(term) ||
                a.service_type?.toLowerCase().includes(term)
            );
        }
    },
    
    // ==========================================================================
    // HEALTH CHECK
    // ==========================================================================
    
    /**
     * Check service health
     */
    checkHealth: async () => {
        console.log('[LOAMS-ENG] Checking service health');
        try {
            const response = await pythonApi.get('/health', { timeout: 10000 });
            return { success: true, ...response.data };
        } catch (error) {
            return { 
                success: false, 
                status: 'unhealthy',
                error: error.message 
            };
        }
    }
};

// Export helper functions for direct use
export { formatFileSize, formatMonthLabel, formatDateDubai };

// Default export
export default loamsEngineerApi;